import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  exportAgentActivityLog,
  getAgentActivityLog,
  postAgentUpdatedFields,
} from '~/api/review';
import { AgentActivityLogEntry } from '~/types/AgentActivityLog';
import { isManuallyAuditedStep } from './agentReviewEdits';

export const agentActivityLogKey = 'agentActivityLog';

export const useAgentActivityLog = (enabled: boolean, stepKey?: string) => {
  return useQuery<AgentActivityLogEntry[]>(
    [agentActivityLogKey, stepKey ?? null],
    () => getAgentActivityLog(stepKey),
    {
      enabled,
      staleTime: 30_000,
    }
  );
};

export const usePostAgentUpdatedFields = () => {
  const queryClient = useQueryClient();
  return useMutation(async (body: Parameters<typeof postAgentUpdatedFields>[0]) => {
    // These review changes are logged atomically by submitStep.
    if (isManuallyAuditedStep(body.stepKey)) return undefined;
    return postAgentUpdatedFields(body);
  }, {
    onSuccess: () => {
      void queryClient.invalidateQueries(agentActivityLogKey);
    },
  });
};

export const useExportAgentActivityLog = () => {
  return useMutation(async () => {
    const response = await exportAgentActivityLog();
    const disposition = (response.headers['content-disposition'] as string) ?? '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?\"?([^\";\n]+)\"?/i);
    const filename = match?.[1] ?? 'agent-activity-log.xlsx';
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};
